import QRCode from "qrcode";
import JSZip from "jszip";
import { v4 as uuidv4 } from "uuid";
import Api from "@/model/api.js";
import Kodo from "@/model/kodo.js";

const extractShortLink = (url) => {
  const matchValue = /.*easylink\.cc\/([a-zA-Z0-9]+).*/.exec(url);
  return matchValue ? matchValue[1] : url;
};

/**
 * 检测当前设备是否为移动端
 * @returns {boolean}
 */
const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
};

/**
 * 将文件名转换为带连字符的格式
 * @param {string} str - 原始文件名
 * @returns {string} 转换后的文件名
 */
const formatFileName = (str) => {
  const extIndex = str.lastIndexOf(".");
  if (extIndex !== -1) {
    const name = str.slice(0, extIndex);
    const ext = str.slice(extIndex + 1);
    return `${name}-${ext}`;
  }
  return `${str} `;
};

/**
 * 生成压缩包文件名
 * @param {string} firstFileName - 第一个文件的名称
 * @param {number} totalFiles - 文件总数
 * @param {string} type - 文件类型标识
 * @param {string} uuid - 唯一标识符
 * @returns {string} 生成的文件名
 */
const createArchiveFileName = (
  firstFileName,
  totalFiles,
  type = "files",
  uuid
) => {
  const fileCountSuffix = totalFiles === 1 ? "file" : "files";
  return `easylink.cc_${formatFileName(
    firstFileName
  )}_${totalFiles}${fileCountSuffix}_${type}_${uuid}.zip`;
};

/**
 * 创建通用压缩包
 * @param {Array<{name: string, data: string, isBase64?: boolean}>} files - 文件列表
 * @returns {Promise<Blob>} 压缩包数据
 */
const createZipArchive = async (files) => {
  const zip = new JSZip();
  files.forEach(({ name, data, isBase64 = false }) => {
    if (isBase64) {
      const base64Content = data.split(",")[1];
      zip.file(name, base64Content, { base64: true });
    } else {
      zip.file(name, data);
    }
  });
  return await zip.generateAsync({ type: "blob" });
};

/**
 * 处理文件下载，根据设备类型使用不同的下载方式
 * @param {string} cdnUrl - CDN文件链接
 */
const openDownloadLink = (cdnUrl) => {
  // 创建一个不可见的 iframe 来处理下载
  const iframe = document.createElement("iframe");
  iframe.style.display = "none";
  iframe.src = cdnUrl;
  document.body.appendChild(iframe);

  // 清理 iframe
  setTimeout(() => {
    document.body.removeChild(iframe);
  }, 5000);

  ElMessage({
    message: "开始下载...",
    type: "info",
    duration: 3000,
  });
};

/**
 * 确保 URL 是完整的链接形式
 * @param {string} url - 输入的 URL（可能是完整链接或短链）
 * @returns {string} 完整的链接
 */
const ensureFullUrl = (url) => {
  if (url.startsWith("http")) {
    return url;
  }
  return `https://easylink.cc/${url}`;
};

export const useQRCode = () => {
  /**
   * 生成包含链接信息的二维码图片
   * @param {string} name - 文件名
   * @param {string} url - 文件链接（可以是完整链接或短链）
   * @returns {Promise<{name: string, data: string}>} 二维码图片信息
   */
  const generateQRCode = async (name, url) => {
    const fullUrl = ensureFullUrl(url);
    const canvas = document.createElement("canvas");
    const qrCodeUrl = await QRCode.toDataURL(fullUrl + "?source=qrcode", {
      width: 400,
      height: 400,
    });

    const img = new Image();
    img.src = qrCodeUrl;

    await new Promise((resolve) => {
      img.onload = resolve;
    });

    canvas.width = img.width;
    canvas.height = img.height;
    const context = canvas.getContext("2d");
    context.drawImage(img, 0, 0);
    context.font = "20px sans-serif";
    context.textAlign = "center";
    context.fillStyle = "white";
    context.fillRect(0, canvas.height - 40, canvas.width, 40);
    context.fillStyle = "#333";
    context.fillText(
      fullUrl.substring(8),
      canvas.width / 2,
      canvas.height - 18
    );

    return {
      name: `${formatFileName(name)}_${extractShortLink(
        url
      )}_easylink.cc_qrcode.png`,
      data: canvas.toDataURL("image/png"),
    };
  };

  /**
   * 下载单个文件的二维码
   * @param {string} name - 文件名
   * @param {string} url - 文件链接（可以是完整链接或短链）
   */
  const downloadQRCode = async (name, url) => {
    try {
      // 1. 生成二维码
      const qrCode = await generateQRCode(name, url);

      // 2. 检查文件是否已存在
      const exists = await Api.checkTempDownloadExists(qrCode.name);
      if (exists) {
        // 如果文件已存在，直接获取下载链接
        const downloadUrl = await Api.getDirectDownloadUrl(
          qrCode.name,
          qrCode.name
        );
        openDownloadLink(downloadUrl);
        return;
      }

      // 3. 转换 base64 为 blob
      const base64Response = await fetch(qrCode.data);
      const blob = await base64Response.blob();

      // 4. 创建文件并上传
      const file = new File([blob], qrCode.name);
      await Kodo.uploadFile(qrCode.name, file, { value: 0 });
      await Api.createTempDownload(qrCode.name);

      // 5. 获取下载链接并下载
      const downloadUrl = await Api.getDirectDownloadUrl(
        qrCode.name,
        qrCode.name
      );
      openDownloadLink(downloadUrl);
    } catch (error) {
      console.error("Download QR code failed:", error);
      const errorMessage = error.message || "下载失败，请重试";
      ElMessage.error(errorMessage);
    }
  };

  /**
   * 下载所有文件的二维码（打包为zip）
   * @param {Array<{name: string, url: string}>} linkList - 文件链接列表
   */
  const downloadAllQRCode = async (linkList) => {
    try {
      // 1. 生成文件名
      const fileName = createArchiveFileName(
        linkList[0].name,
        linkList.length,
        "qrcodes",
        uuidv4()
      );

      // 2. 生成所有二维码
      const qrCodeUrls = await Promise.all(
        linkList.map((link) => generateQRCode(link.name, link.url))
      );

      // 3. 创建并上传压缩包
      const blob = await createZipArchive(
        qrCodeUrls.map((item) => ({
          ...item,
          isBase64: true,
        }))
      );
      const file = new File([blob], fileName);
      await Kodo.uploadFile(fileName, file, { value: 0 });
      await Api.createTempDownload(fileName);

      // 4. 获取下载链接并下载
      const downloadUrl = await Api.getDirectDownloadUrl(fileName, fileName);
      openDownloadLink(downloadUrl);
    } catch (error) {
      console.error("Download QR codes failed:", error);
      const errorMessage = error.message || "下载失败，请重试";
      ElMessage.error(errorMessage);
    }
  };

  return {
    downloadQRCode,
    downloadAllQRCode,
  };
};
